import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { FileSearch, Loader2, Gauge, Stamp, AlertTriangle, Layers } from "lucide-react";
import { PageHeader } from "@/components/common/PageHeader";
import { DataTable } from "@/components/common/DataTable";
import { StatusBadge, RiskBadge } from "@/components/common/StatusBadge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { RequisitosCredito } from "@/components/common/RequisitosCredito";
import { SolicitudPldPanel } from "@/components/solicitudes/SolicitudPldPanel";
import { SicBloqueOriginacion } from "@/components/originacion/SicBloqueOriginacion";
import { AutorizacionOcSolicitud } from "@/components/solicitudes/AutorizacionOcSolicitud";
import { AvisosDatosFiscales } from "@/components/fiscal/AvisosDatosFiscales";
import { api, listResource } from "@/lib/api";
import { mxn, formatearPlazo } from "@/lib/format";

const COLS = [
  { key: "cliente", label: "Cliente" },
  { key: "producto", label: "Producto" },
  { key: "monto_solicitado", label: "Monto", kind: "currency" },
  { key: "plazo", label: "Plazo", render: (r) => formatearPlazo(r.plazo, r.periodicidad) },
  { key: "estatus", label: "Estatus", kind: "badge" },
];

const REC_COLOR = {
  "Aprobable": "green", "Aprobable condicionado": "amber", "Requiere revisión": "amber", "No recomendable": "red",
};

export default function Originacion() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [sel, setSel] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const load = useCallback(() => {
    setLoading(true); setError(false);
    return listResource("solicitudes").then(setRows).catch(() => setError(true)).finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  // sep-2026 · al abrir se relee el detalle: `datos_faltantes` se calcula EN VIVO en el
  // GET singular (server.py), así el panel del comité nunca muestra la foto rancia que
  // viaja en el listado.
  const open = async (row) => {
    setSel(row);
    try {
      const fresh = await api.get(`/solicitudes/${row.id}`).then((r) => r.data);
      setSel(fresh);
    } catch { /* se queda con la fila del listado */ }
  };

  const refresh = async () => {
    if (!sel?.id) return;
    // oct-2026 · el detalle puede responder 404 a un rol owner-scoped (FASE 2). Si falla,
    // se conserva lo que ya está en pantalla: el historial SIC lo recarga su propio bloque.
    try {
      const fresh = await api.get(`/solicitudes/${sel.id}`).then((r) => r.data);
      setSel(fresh);
    } catch { /* se queda con el detalle actual */ }
    load();
  };

  const a = sel?.analisis;

  return (
    <div>
      <PageHeader title="Originación + Consulta SIC"
        subtitle="Prospecto → Solicitud → Autorización SIC → Consulta Círculo/Buró → Análisis → Comité" />
      <div className="p-6 md:p-8">
        <DataTable testid="table-originacion" columns={COLS} rows={rows} onRowClick={open}
          loading={loading} error={error} onRetry={load}
          emptyLabel="Sin solicitudes. Crea una desde Solicitudes de crédito o el Simulador." />
        <p className="mt-3 text-xs text-slate-400">Haz clic en una solicitud para ejecutar la consulta SIC, ver el análisis asistido y registrar la decisión del comité.</p>
      </div>

      <Dialog open={!!sel} onOpenChange={(v) => !v && setSel(null)}>
        <DialogContent className="max-h-[92vh] overflow-y-auto sm:max-w-3xl">
          {sel && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2 font-heading">
                  <FileSearch className="h-5 w-5 text-primary" /> {sel.cliente || "—"} — {sel.producto || sel.tipo || "—"}
                </DialogTitle>
                <DialogDescription className="sr-only">Consulta SIC, análisis, comité y requisitos del crédito de la solicitud</DialogDescription>
              </DialogHeader>

              <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                {[["Monto solicitado", mxn(sel.monto_solicitado)], ["Plazo", formatearPlazo(sel.plazo, sel.periodicidad)], ["Tasa", sel.tasa_ordinaria != null ? `${sel.tasa_ordinaria}%` : "—"], ["Estatus", sel.estatus]].map(([l, v]) => (
                  <div key={l} className="rounded-md border border-slate-200 bg-slate-50 p-2.5">
                    <p className="text-[10px] uppercase tracking-wide text-slate-500">{l}</p>
                    <p className="mt-0.5 text-sm font-semibold text-slate-800">{v}</p>
                  </div>
                ))}
              </div>

              {/* PLD/EBR del cliente — resultado reutilizado, sin reconsultar al proveedor */}
              <SolicitudPldPanel solicitud={sel} />

              {/* feb-2026 · panel persistente Datos faltantes — visible antes de la decisión
                  del comité para que el operador vea de un vistazo qué corregir. Se llena
                  server-side en cada create/update de la solicitud. */}
              {Array.isArray(sel.datos_faltantes) && sel.datos_faltantes.length > 0 && (
                <div className="rounded-lg border border-rose-200 bg-rose-50/60 p-4"
                     data-testid="datos-faltantes-panel">
                  <div className="mb-2 flex items-center gap-2 text-rose-800">
                    <AlertTriangle className="h-4 w-4" />
                    <p className="font-heading text-sm font-semibold">
                      Datos faltantes para autorizar y contratar
                    </p>
                  </div>
                  <div className="space-y-2">
                    {sel.datos_faltantes.map((b) => (
                      <div key={`df-${b.cliente_id}`}
                           data-testid={`datos-faltantes-integrante-${b.cliente_id}`}
                           className="rounded-md border border-rose-200 bg-white/70 p-2 text-sm">
                        <div className="mb-1 flex items-center gap-2">
                          <span className="font-medium text-rose-900">{b.cliente}</span>
                          <span className={`rounded px-1.5 py-0.5 text-[10px] font-medium ${
                            b.rol === "titular" ? "bg-rose-700 text-white" : "bg-slate-200 text-slate-700"
                          }`}>
                            {b.rol === "titular" ? "Titular" : "Obligado solidario"}
                          </span>
                        </div>
                        <ul className="list-inside list-disc text-[13px] text-rose-800">
                          {(b.problemas || []).map((p, i) => (
                            <li key={`df-p-${b.cliente_id}-${i}`}>{p}</li>
                          ))}
                        </ul>
                      </div>
                    ))}
                  </div>
                  <p className="mt-2 text-[11px] text-slate-500">
                    El Comité no podrá aprobar mientras existan datos incompletos.
                  </p>
                </div>
              )}

              <AvisosDatosFiscales avisos={sel.avisos_fiscales} testid="originacion-avisos-fiscales" />

              {/* oct-2026 · Salida del aviso rojo: la decisión ÚNICA del Oficial de
                  Cumplimiento (autorizar con carta PDF + motivo, o no autorizar con motivo).
                  Los botones solo los pinta ese rol; los demás ven el resultado y, si está
                  pendiente, por qué no se puede enviar al Comité. Misma decisión que en
                  «Solicitudes de crédito»: si se decide allá, aquí aparece el resultado. */}
              <AutorizacionOcSolicitud solicitud={sel} onDone={refresh} />

              {/* sep-2026 · MULTICRÉDITO — aviso INFORMATIVO para el Comité: un cliente puede
                  tener varios créditos a la vez. NO bloquea nada; solo da el dato al decidir. */}
              {sel.creditos_cliente?.activos > 0 && (
                <div className="rounded-lg border border-sky-200 bg-sky-50/70 p-4"
                     data-testid="creditos-cliente-aviso">
                  <div className="mb-1 flex items-center gap-2 text-sky-900">
                    <Layers className="h-4 w-4" />
                    <p className="font-heading text-sm font-semibold">
                      El cliente ya tiene {sel.creditos_cliente.vigentes} crédito{sel.creditos_cliente.vigentes === 1 ? "" : "s"} vigente{sel.creditos_cliente.vigentes === 1 ? "" : "s"}
                      {" · Saldo total "}
                      <span data-testid="creditos-cliente-saldo">{mxn(sel.creditos_cliente.saldo_total)}</span>
                    </p>
                  </div>
                  <ul className="space-y-0.5 text-[13px] text-sky-900">
                    {sel.creditos_cliente.creditos.map((c) => (
                      <li key={`cc-${c.id}`} data-testid={`creditos-cliente-fila-${c.numero_credito}`}>
                        <span className="font-mono text-xs">{c.numero_credito}</span>
                        {" · "}{c.estatus}{" · "}{mxn(c.saldo_insoluto)}
                        {c.producto ? ` · ${c.producto}` : ""}
                      </li>
                    ))}
                  </ul>
                  <p className="mt-2 text-[11px] text-slate-500">
                    Informativo: un cliente puede tener varios créditos a la vez. Este aviso no impide autorizar.
                  </p>
                </div>
              )}

              {/* Consulta SIC — Fase 8+ (feb-2026). Bloque rediseñado. Reemplaza los
                  dos botones rotos anteriores ("Generar autorización" 400 · "Consultar
                  (simulación)" 409) por un componente aditivo que ofrece: (1) autorización
                  del cliente con 3 caminos y evidencia obligatoria en positivo, (2) consulta
                  API solo cuando la integración está conectada de verdad (nada de "simulación"),
                  (3) consulta manual subiendo el PDF del portal del buró. */}
              <SicBloqueOriginacion solicitud={sel} onChanged={refresh} />

              {/* Panel legacy: si la solicitud ya trae análisis SIC previo, seguirlo mostrando
                  para no perder trazabilidad del scoring interno calculado por consultas API. */}
              {sel.sic && (
                <div className="mt-3 grid grid-cols-2 gap-2 rounded-lg border border-slate-200 bg-white p-3 sm:grid-cols-4 text-sm"
                     data-testid="orig-sic-resumen-legacy">
                  <div><span className="text-slate-400 text-xs">Folio</span><p className="font-mono text-xs">{sel.sic.folio}</p></div>
                  <div><span className="text-slate-400 text-xs">Score</span><p className="font-semibold">{sel.sic.score}</p></div>
                  <div><span className="text-slate-400 text-xs">Resultado</span><div className="mt-0.5"><StatusBadge value={sel.sic.resultado} /></div></div>
                  <div><span className="text-slate-400 text-xs">No hit</span><p>{sel.sic.no_hit ? "Sí" : "No"}</p></div>
                </div>
              )}

              {/* Análisis */}
              {a && (
                <div className="rounded-lg border border-slate-200 bg-white p-4">
                  <h4 className="mb-3 flex items-center gap-2 font-heading text-sm font-semibold text-slate-800"><Gauge className="h-4 w-4 text-primary" /> Análisis de crédito (asistido)</h4>
                  <div className="grid grid-cols-2 gap-2 sm:grid-cols-4 text-sm">
                    {[["Créditos abiertos", a.creditos_abiertos], ["Créditos vencidos", a.creditos_vencidos], ["Peor atraso", `${a.peor_atraso_dias} d`], ["Consultas recientes", a.consultas_recientes], ["Endeudamiento", mxn(a.endeudamiento_estimado)], ["MOP", a.mop], ["Score interno", a.score_interno]].map(([l, v]) => (
                      <div key={l} className="rounded-md border border-slate-100 bg-slate-50 p-2">
                        <p className="text-[10px] uppercase tracking-wide text-slate-500">{l}</p>
                        <p className="mt-0.5 text-sm font-semibold text-slate-800">{v}</p>
                      </div>
                    ))}
                    <div className="rounded-md border border-slate-100 bg-slate-50 p-2">
                      <p className="text-[10px] uppercase tracking-wide text-slate-500">Riesgo sugerido</p>
                      <div className="mt-0.5"><RiskBadge value={a.riesgo_sugerido === "Bajo" ? "Bajo" : a.riesgo_sugerido === "Alto" ? "Alto" : "Medio"} /></div>
                    </div>
                  </div>
                  <div className="mt-3 flex items-center justify-between rounded-md border-2 px-3 py-2"
                    style={{ borderColor: REC_COLOR[a.recomendacion] === "green" ? "#10B981" : REC_COLOR[a.recomendacion] === "red" ? "#DC2626" : "#F59E0B" }}>
                    <div>
                      <p className="text-[11px] uppercase tracking-wide text-slate-500">Recomendación preliminar</p>
                      <p className="font-heading text-base font-semibold text-slate-900">{a.recomendacion}</p>
                    </div>
                    <span className="text-[11px] text-slate-400">{a.nota}</span>
                  </div>
                </div>
              )}

              {/* Comité — SOLO LECTURA (sep-2026). La mesa de decisión vive en su módulo
                  independiente («Comité de Crédito»), con acceso por rol y área, cuatro ojos
                  e historial. Aquí solo se muestra la última decisión: sin botones. */}
              <div className="rounded-lg border border-slate-200 bg-white p-4" data-testid="comite-solo-lectura">
                <h4 className="mb-3 flex items-center gap-2 font-heading text-sm font-semibold text-slate-800"><Stamp className="h-4 w-4 text-primary" /> Comité de crédito</h4>
                {sel.comite ? (
                  <div className="space-y-1 text-sm text-slate-600">
                    <p data-testid="comite-ultima-decision">
                      Última decisión: <StatusBadge value={sel.comite.decision} /> por {sel.comite.usuario}
                      {sel.comite.fecha ? ` · ${sel.comite.fecha}` : ""}
                      {sel.comite.autorizacion_propia && (
                        <span data-testid="comite-marca-autorizacion-propia"
                              className="ml-2 rounded bg-amber-600 px-1.5 py-0.5 text-[10px] font-medium text-white">
                          {sel.comite.marca || "Autorización propia"}
                        </span>
                      )}
                    </p>
                    {sel.comite.comentarios && <p className="text-[13px]">{sel.comite.comentarios}</p>}
                    {sel.comite.justificacion && (
                      <p className="text-[13px] text-amber-900">Justificación: {sel.comite.justificacion}</p>
                    )}
                    {(sel.comite_historial || []).length > 1 && (
                      <p className="text-[11px] text-slate-400">
                        {(sel.comite_historial || []).length} decisiones registradas en el historial del módulo.
                      </p>
                    )}
                  </div>
                ) : (
                  <p className="text-sm text-slate-500" data-testid="comite-sin-decision">Sin decisión del Comité.</p>
                )}
                <p className="mt-2 text-[11px] text-slate-400">
                  Solo lectura. La decisión se registra en el módulo <strong>Comité de Crédito</strong> por
                  el analista asignado (regla de cuatro ojos: quien capturó la solicitud no la decide).
                </p>
              </div>

              {/* Requisitos dinámicos + Contratar */}
              <RequisitosCredito solicitud={sel} onContratado={refresh} onIntegrantes={refresh}
                onLineaGenerada={(linea) => { setSel(null); navigate(`/lineas/${linea.id}`); }} />
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
